import type { SubmitHandler } from 'react-hook-form';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateAppSettingsResponseBody } from '~/lib/api/modules/AppSettings';
import type { IUseUpdateAppSettingsMutationValues } from '~/lib/api/modules/AppSettings/hooks/updateAppSettingsMutation';
import type { ITabsSettingsFormValues } from './TabsSettingsForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppPreviewState } from '~/components/organisms/AppPreview';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppSettingsMutation } from '~/lib/api/modules/AppSettings/hooks/updateAppSettingsMutation';
import { TabsSettingsForm } from './TabsSettingsForm';

export type IUpdateTabsSettingsFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateAppSettingsResponseBody,
    inputValues: IUseUpdateAppSettingsMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const UpdateTabsSettingsForm: React.FC<IUpdateTabsSettingsFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const updateAppSettingsMutation = useUpdateAppSettingsMutation();
  const appQuery = useAppQuery({ appId });
  const appPreviewState = useAppPreviewState();
  const app = appQuery.data;
  const appSettings = app?.settings;

  const handleSubmit: SubmitHandler<ITabsSettingsFormValues> = (values) => {
    if (updateAppSettingsMutation.isLoading || !appSettings) {
      return;
    }

    return updateAppSettingsMutation.mutateAsync(
      {
        appId,
        body: {
          ...appSettings,
          press: {
            ...appSettings.press,
            env: {
              ...appSettings.press?.env,
              startTab: values.startTabId,
              tabOrder: values.tabIds?.join(','),
            },
          },
        },
      },
      {
        onSuccess: (outputData, inputValues) => {
          onSuccess?.(outputData, inputValues);
          appPreviewState.iframeRef?.current?.contentWindow?.postMessage(
            {
              action: 'refreshAppSettings',
              crowdaa: true,
            },
            '*',
          );
        },
      },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
        first
      >
        {appSettings ? (
          <TabsSettingsForm
            first
            mode="edition"
            defaultValues={{
              tabIds: appSettings?.press?.env?.tabOrder?.split(','),
              startTabId: appSettings.press?.env?.startTab ?? 'today',
              merchMMFEnabled: !!app.credentials?.mmf?.id,
              merchWPEnabled: !!appSettings?.press?.env?.merchWPUrl,
              chatEnabled: !!app?.credentials?.firebase?.webAppConfig,
              leaderboardEnabled: !!appSettings?.press?.env?.leaderboardWpUrl,
            }}
            cancelUrl={cancelUrl}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
